import type { Anniversary, CoupleMeta } from '@us/shared';

const DAY = 86_400_000;

function startOfDay(d: Date): number {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
}

/** 사귄 시작일로부터 오늘까지 며칠째인지(시작일=1일째). */
export function daysTogether(startDate: string, now = new Date()): number {
  const start = startOfDay(new Date(startDate + 'T00:00:00'));
  const today = startOfDay(now);
  return Math.floor((today - start) / DAY) + 1;
}

export interface Milestone {
  label: string;
  date: string; // YYYY-MM-DD
  daysLeft: number;
}

function fmt(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(
    d.getDate(),
  ).padStart(2, '0')}`;
}

/** 다음 100일 단위 마일스톤(예: 2500일)까지 D-카운트. */
export function nextDayMilestone(startDate: string, now = new Date()): Milestone {
  const elapsed = daysTogether(startDate, now);
  const nextCount = Math.ceil((elapsed + 1) / 100) * 100;
  const start = new Date(startDate + 'T00:00:00');
  const target = new Date(start.getTime() + (nextCount - 1) * DAY);
  const daysLeft = Math.round((startOfDay(target) - startOfDay(now)) / DAY);
  return { label: `${nextCount.toLocaleString()}일`, date: fmt(target), daysLeft };
}

/** 다가오는 기념일(반복은 올해/내년으로 환산) 중 가장 가까운 것. */
export function upcomingAnniversary(
  anniversaries: Anniversary[],
  now = new Date(),
): Milestone | null {
  const today = startOfDay(now);
  let best: Milestone | null = null;
  for (const a of anniversaries) {
    const base = new Date(a.date + 'T00:00:00');
    let when = startOfDay(base);
    if (a.recurring) {
      const y = now.getFullYear();
      let cand = startOfDay(new Date(y, base.getMonth(), base.getDate()));
      if (cand < today) cand = startOfDay(new Date(y + 1, base.getMonth(), base.getDate()));
      when = cand;
    }
    if (when < today) continue;
    const daysLeft = Math.round((when - today) / DAY);
    if (!best || daysLeft < best.daysLeft) {
      best = { label: a.label, date: fmt(new Date(when)), daysLeft };
    }
  }
  return best;
}

export type MilestoneKind = 'day' | 'year' | 'anniversary';
export interface UpcomingItem extends Milestone {
  kind: MilestoneKind;
}

/**
 * 홈 "다가오는 날들" 목록. 가까운 순으로 모아 준다.
 *  - 100일 단위 마일스톤 2개(예: 2300일, 2400일)
 *  - 다음 주년(사귄 날 기준)
 *  - 등록된 기념일(반복은 올해/내년으로 환산, 지난 1회성은 제외)
 */
export function upcomingMilestones(
  meta: CoupleMeta,
  now = new Date(),
  limit = 5,
): UpcomingItem[] {
  const today = startOfDay(now);
  const start = new Date(meta.startDate + 'T00:00:00');
  const items: UpcomingItem[] = [];

  // 100일 단위 2개
  const elapsed = daysTogether(meta.startDate, now);
  const baseCount = Math.ceil((elapsed + 1) / 100) * 100;
  for (let k = 0; k < 2; k++) {
    const count = baseCount + k * 100;
    const target = new Date(start.getTime() + (count - 1) * DAY);
    const daysLeft = Math.round((startOfDay(target) - today) / DAY);
    items.push({ kind: 'day', label: `${count.toLocaleString()}일`, date: fmt(target), daysLeft });
  }

  // 다음 주년(사귄 날의 월·일 기준)
  {
    const y = now.getFullYear();
    let when = startOfDay(new Date(y, start.getMonth(), start.getDate()));
    if (when < today) when = startOfDay(new Date(y + 1, start.getMonth(), start.getDate()));
    const years = new Date(when).getFullYear() - start.getFullYear();
    if (years >= 1) {
      const daysLeft = Math.round((when - today) / DAY);
      items.push({ kind: 'year', label: `${years}주년`, date: fmt(new Date(when)), daysLeft });
    }
  }

  // 등록 기념일
  for (const a of meta.anniversaries) {
    const b = new Date(a.date + 'T00:00:00');
    let when = startOfDay(b);
    if (a.recurring) {
      const y = now.getFullYear();
      let cand = startOfDay(new Date(y, b.getMonth(), b.getDate()));
      if (cand < today) cand = startOfDay(new Date(y + 1, b.getMonth(), b.getDate()));
      when = cand;
    }
    if (when < today) continue;
    const daysLeft = Math.round((when - today) / DAY);
    items.push({ kind: 'anniversary', label: a.label, date: fmt(new Date(when)), daysLeft });
  }

  // 같은 날·같은 라벨 중복 제거 후 가까운 순
  const seen = new Set<string>();
  return items
    .sort((x, y) => x.daysLeft - y.daysLeft)
    .filter((it) => {
      const key = `${it.date}|${it.label}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    })
    .slice(0, limit);
}

export function summarize(meta: CoupleMeta, now = new Date()) {
  return {
    days: daysTogether(meta.startDate, now),
    milestone: nextDayMilestone(meta.startDate, now),
    anniversary: upcomingAnniversary(meta.anniversaries, now),
  };
}
